// src/pages/LandingPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import TarotCard from '../components/TarotCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import VoiceReader from '../components/ui/VoiceReader';
import { ReferralCreateModal, ReferralStats } from '../components/ReferralSystem';
import {
  TarotCardsIcon,
  GraduationCapIcon,
  BookIcon,
  JournalIcon,
  ZodiacWheelIcon,
  GiftIcon,
  LockIcon,
  RobotIcon,
  StarIcon,
} from '../components/ui/Icons';
import useScrollReveal from '../utils/useScrollReveal';

const shuffleDeck = (deck) => {
  let shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const LandingPage = ({ onNavigate, aiStatus, userDetails }) => {
  const deckCards = useSelector((state) => state.tarot.cards);
  const [cardOfTheDay, setCardOfTheDay] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showMeaning, setShowMeaning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const featuresRef = useRef(null);

  const aiOnline = aiStatus === 'online';

  // Se re-ejecuta cuando isLoading pasa a false: recién ahí el grid de
  // feature cards existe en el DOM y featuresRef.current deja de ser null
  // (antes de eso se muestra el LoadingSpinner en su lugar).
  useScrollReveal(featuresRef, '.scroll-reveal', [isLoading]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const storedData = localStorage.getItem('tarotDailyCard');
      if (storedData) {
        const { date, card } = JSON.parse(storedData);
        if (date === today) {
          setCardOfTheDay(card);
          setIsFlipped(true);
          setShowMeaning(true);
          setIsLoading(false);
          return;
        }
      }
    } catch (e) {
      console.error('Error leyendo carta del día:', e);
    }
    const shuffled = shuffleDeck(deckCards);
    const newCard = shuffled[0];
    setCardOfTheDay(newCard);
    setIsLoading(false);
    try {
      localStorage.setItem('tarotDailyCard', JSON.stringify({ date: today, card: newCard }));
    } catch (e) {
      console.error('Error guardando carta del día:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlip = () => {
    if (!isFlipped) {
      setIsFlipped(true);
      setTimeout(() => setShowMeaning(true), 600);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <LoadingSpinner message="Preparando tu carta del día..." />
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-blobs" aria-hidden="true">
          <span className="hero-blob hero-blob--primary"></span>
          <span className="hero-blob hero-blob--gold"></span>
          <span className="hero-blob hero-blob--primary-2"></span>
        </div>
        <div className="hero-stars"></div>
        <div className="hero-content">
          <p className="hero-eyebrow">✦ Oráculo Digital ✦</p>
          <h1 className="hero-title font-cinzel">
            {showMeaning ? (
              <>
                {userDetails?.name
                  ? `Bienvenida de nuevo, ${userDetails.name.split(' ')[0]}`
                  : 'Tu Guía para Hoy'}
              </>
            ) : (
              'Descifra tu Destino'
            )}
          </h1>
          <p className="hero-subtitle">
            {showMeaning
              ? 'Tu mensaje del día te espera. ¿Qué camino eliges a continuación?'
              : 'Respira profundo, enfoca tu energía y haz clic en la carta para revelar el mensaje que el universo tiene para ti hoy.'}
          </p>
        </div>
      </section>

      {/* Carta del Día */}
      <section className="daily-card-section">
        <div className="daily-card-container">
          {cardOfTheDay ? (
            <div className="daily-card-wrapper">
              <TarotCard card={cardOfTheDay} isFlipped={isFlipped} onFlip={handleFlip} />
              {!isFlipped && (
                <div className="card-tap-hint">
                  <span className="tap-pulse">Toca para revelar</span>
                </div>
              )}
            </div>
          ) : (
            <LoadingSpinner />
          )}
        </div>

        {showMeaning && cardOfTheDay && (
          <div className="daily-card-meaning">
            <h2 className="daily-card-name font-cinzel">{cardOfTheDay.name}</h2>
            <p className="daily-card-quote">"{cardOfTheDay.meaning_up}"</p>
            <VoiceReader
              text={`Tu carta del día es ${cardOfTheDay.name}. Su mensaje es: ${cardOfTheDay.meaning_up}. ${cardOfTheDay.desc || ''}`}
              label="Escuchar mensaje"
            />
            <div className="landing-cta-buttons">
              <button
                onClick={() => onNavigate('startFreeReading')}
                className="main-button primary-button"
              >
                ✨ Ver Lectura Completa Gratis
              </button>
              <button
                onClick={() => onNavigate('spreadSelection')}
                className="main-button secondary-button"
              >
                Explorar Todas las Tiradas
              </button>
            </div>
          </div>
        )}
      </section>

      {/* AI Status Banner */}
      {aiStatus === 'checking' && (
        <div className="ai-status-banner ai-status-banner--checking">
          <span className="status-dot status-dot--pulse"></span>
          Conectando con el Oráculo AI...
        </div>
      )}
      {aiOnline && (
        <div className="ai-status-banner ai-status-banner--online">
          <span className="status-dot status-dot--online"></span>
          Oráculo AI activo — Interpretaciones profundas disponibles
        </div>
      )}
      {aiStatus === 'offline' && (
        <div className="ai-status-banner ai-status-banner--offline">
          <span className="status-dot status-dot--offline"></span>
          Oráculo AI no disponible — Solo lecturas locales disponibles
        </div>
      )}

      {/* Feature Cards */}
      <section className="landing-features" ref={featuresRef}>
        <button onClick={() => onNavigate('spreadSelection')} className="feature-card feature-card--tiradas scroll-reveal">
          <div className="feature-icon"><TarotCardsIcon /></div>
          <h3 className="feature-title font-cinzel">Tiradas Premium</h3>
          <p className="feature-desc">
            Amor, negocios, salud y más. Interpretadas por IA especializada.
          </p>
          <span className="feature-arrow">→</span>
        </button>
        <button onClick={() => onNavigate('courses')} className="feature-card feature-card--gold feature-card--cursos scroll-reveal">
          <div className="feature-icon"><GraduationCapIcon /></div>
          <h3 className="feature-title font-cinzel">Cursos de Tarot</h3>
          <p className="feature-desc">
            Aprende a leer el tarot desde cero con nuestra metodología intuitiva.
          </p>
          <span className="feature-arrow">→</span>
        </button>
        <button onClick={() => onNavigate('guide')} className="feature-card feature-card--guia scroll-reveal">
          <div className="feature-icon"><BookIcon /></div>
          <h3 className="feature-title font-cinzel">Guía Completa</h3>
          <p className="feature-desc">
            Explora el significado de las 78 cartas en ambas posiciones.
          </p>
          <span className="feature-arrow">→</span>
        </button>
        <button onClick={() => onNavigate('journal')} className="feature-card feature-card--diario scroll-reveal">
          <div className="feature-icon"><JournalIcon /></div>
          <h3 className="feature-title font-cinzel">Mi Diario</h3>
          <p className="feature-desc">
            Registra y revive tus lecturas pasadas para descubrir patrones.
          </p>
          <span className="feature-arrow">→</span>
        </button>
        <button onClick={() => onNavigate('zodiac')} className="feature-card feature-card--zodiaco scroll-reveal">
          <div className="feature-icon"><ZodiacWheelIcon /></div>
          <h3 className="feature-title font-cinzel">Zodiaco y Astrología</h3>
          <p className="feature-desc">
            Descubre los 12 signos zodiacales, sus elementos, compatibilidades y su conexión con el tarot.
          </p>
          <span className="feature-arrow">→</span>
        </button>
      </section>

      {/* Referral Section */}
      <section className="landing-referral">
        <ReferralStats />
        <button
          onClick={() => setShowReferralModal(true)}
          className="feature-card feature-card--referral"
        >
          <div className="feature-icon"><GiftIcon /></div>
          <h3 className="feature-title font-cinzel">Ganá Compartiendo</h3>
          <p className="feature-desc">
            Creá tu código único y ganá el 20% de comisión por cada persona que compre con tu código.
          </p>
          <span className="feature-arrow">→</span>
        </button>
      </section>

      {showReferralModal && (
        <ReferralCreateModal onClose={() => setShowReferralModal(false)} />
      )}

      {/* Trust Section */}
      <section className="landing-trust">
        <div className="trust-item">
          <span className="trust-icon"><LockIcon /></span>
          <p>Tus datos están protegidos — nunca los compartimos ni los vendemos a terceros</p>
        </div>
        <div className="trust-item">
          <span className="trust-icon"><RobotIcon /></span>
          <p>Interpretaciones generadas por IA avanzada (Groq / LLaMA 3)</p>
        </div>
        <div className="trust-item">
          <span className="trust-icon"><StarIcon /></span>
          <p>78 cartas auténticas del Tarot Rider-Waite</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-legal-links">
          <button className="footer-legal-link" onClick={() => onNavigate('legal:terminos')}>
            Términos
          </button>
          <button className="footer-legal-link" onClick={() => onNavigate('legal:privacidad')}>
            Privacidad
          </button>
          <button className="footer-legal-link" onClick={() => onNavigate('legal:reembolsos')}>
            Reembolsos
          </button>
        </div>
        <div className="footer-content">
          <div className="footer-brand">
            <h3 className="footer-title font-cinzel">Tarot Místico</h3>
            <p className="footer-tagline">Tu guía espiritual digital</p>
          </div>
          <div className="footer-contact">
            <p className="footer-contact-title">Contacto</p>
            <a href="mailto:contacto@tarotmistico.lat" className="footer-link">
              contacto@tarotmistico.lat
            </a>
            <a href="mailto:support@tarotmistico.lat" className="footer-link">
              support@tarotmistico.lat
            </a>
          </div>
          <div className="footer-contact">
            <p className="footer-contact-title">Pagos</p>
            <a href="mailto:pagos@tarotmistico.lat" className="footer-link">
              pagos@tarotmistico.lat
            </a>
            <p className="footer-note">Aceptamos Mercado Pago y PayPal</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} tarotmistico.lat — Todos los derechos reservados</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
